import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Row, Col } from 'reactstrap';

import { IRootState } from '@app/store';
import { changePasswordRequest } from '@reducers/auth/AuthAction';
import { handleError } from '@services/ErrorHandler';
import { CustomButton } from '@shared/buttons';
import InputField, { InputFieldProps } from '@shared/components/input/InputField';
import useTranslation from '@shared/hooks/useTranslation';
import {
  CloseIcon, BackIcon,
} from '@shared/icons';

type IForgotPasswordContent = {
  onNext: () => void;
  onBack: () => void;
  onToggle: () => void;
};
interface FormFields {
  password: string;
  password_confirmation: string;
}

const ChangePasswordContent = (props: IForgotPasswordContent) => {
  const i18n = useTranslation('general');
  const i18nForgotPassword = useTranslation('forgotPassword');
  const { onBack, onNext, onToggle } = props;
  const [ isChangingPassword, setChangingPassword ] = useState(false);
  const dispatch = useDispatch<any>();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { email, account_reset_id, verification_code } = useSelector(({ Auth }: IRootState) => ({
    codeValidityInSeconds: Auth.codeValidityInSeconds,
    email: Auth.forgotPassword.email,
    account_reset_id: Auth.forgotPassword.account_reset_id,
    verification_code: Auth.forgotPassword.verification_code,
  }));

  const [ formData, setFormData ] = useState<FormFields>({
    password: '',
    password_confirmation: '',
  });

  const [ errors, setErrors ] = useState<any>({
    password: '',
    password_confirmation: '',
  });

  const FORM_FIELDS: Array<InputFieldProps & { name: keyof FormFields }> = useMemo(() => [
    {
      id: 'input-password',
      name: 'password',
      label: i18n.input.password.label,
      placeholder: i18n.input.password.placeholder,
      type: 'password',
      autoComplete: 'new-password',
      isPasswordStrength: true,
      value: formData?.password,
    },
    {
      id: 'input-confirm-password',
      name: 'password_confirmation',
      label: i18n.input.confirm_password.label,
      placeholder: i18n.input.confirm_password.placeholder,
      type: 'password',
      value: formData?.password_confirmation,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ formData ]);

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setChangingPassword(true);
    try {
      const data = {
        // eslint-disable-next-line camelcase
        id: account_reset_id,
        email,
        verification_code,
        ...formData,
      };
      await dispatch(changePasswordRequest(data)).$promise;
      onNext();
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;
      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          return;
        }
        handleError(error);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const isFieldsEmpty = useMemo(() => Object.values(formData).some(
    (value) => isEmpty(value)),
  [ formData ]);

  const isPasswordValid = useMemo(() => {
    const isValidLength = formData?.password?.length >= 8;
    const isValidCase = /[A-Z]/.test(formData?.password);
    const isValidNumber = /[0-9]/.test(formData?.password);
    // include all special characters except a-z and 0-9
    const isValidChar = /[^A-Za-z0-9]/.test(formData?.password);
    return isValidLength && isValidCase && isValidNumber && isValidChar;
  }, [ formData?.password ]);

  return (
    <div className="change-password-content-container">
      <Row className="mb-3">
        <Col xs={6}>
          <CustomButton
            id="btn-back"
            className="btn-lg fp-back-btn"
            color="secondary"
            onClick={onBack}
          >
            <BackIcon /> {i18n.button.back}
          </CustomButton>
        </Col>
        <Col xs={6} className="fp-close-button-style">
          <a aria-label="Close" role="button" href="#" onClick={onToggle}><CloseIcon /></a>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="s2 change-password-modal-title mb-5">{i18nForgotPassword.title}</div>
          <p className="b1 mb-2">{i18nForgotPassword.change_password.description}</p>
        </Col>
      </Row>
      <Form id="change-password-form" noValidate onSubmit={handleChangePassword}>
        {FORM_FIELDS.map((field: InputFieldProps) => (
          <InputField
            {...field}
            key={field.id}
            fieldError={errors[field.name] ?? ''}
            onInputChangeHandler={(value) => setFormData({ ...formData, [field.name]: value })}
          />
        ))}
        <div className="button-container">
          <CustomButton
            id="btn-verify"
            type="submit"
            className="btn-lg w-100"
            loading={isChangingPassword}
            disabled={isFieldsEmpty || !isPasswordValid}
            color="primary"
          >{i18nForgotPassword.button.confirm_password}
          </CustomButton>
        </div>
      </Form>
    </div>
  );
};

export default ChangePasswordContent;
